import DescriptionIcon from '@mui/icons-material/Description';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import React, { useRef, useState } from 'react';
import {
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tooltip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AddIcon from '@mui/icons-material/Add';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import GridOnIcon from '@mui/icons-material/GridOn';
import { getFileTypes } from '../utils/helpers';

const Attachments = ({ files, onAddFile, onDeleteFile, loading, taskId, userId }) => {
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [fileToDelete, setFileToDelete] = useState(null);
  const fileInputRef = useRef(null);

  const handleDeleteClick = (file) => setFileToDelete(file);
  const handleCancelDelete = () => setFileToDelete(null);
  const handleConfirmDelete = () => {
    onDeleteFile(fileToDelete);
    setFileToDelete(null);
  };

  const handleAddIconClick = () => fileInputRef.current.click();

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setOpenImageDialog(true);
  };

  const handleDownloadFile = async (file) => {
    if (!file?.location) return;
    try {
      const response = await fetch(file.location);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.fileName || 'downloaded-file';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      window.open(file.location, '_blank');
    }
  };

  const renderFileItem = (file, index) => {
    const fileType = getFileTypes(file.location);
    const fileName = file?.fileName || 'Unnamed File';

    let FileIconComponent = InsertDriveFileIcon;
    let iconColor = '#64748B';

    if (fileType === 'pdf')   { FileIconComponent = PictureAsPdfIcon;    iconColor = '#EF4444'; }
    else if (fileType === 'excel') { FileIconComponent = GridOnIcon;      iconColor = '#10B981'; }
    else if (fileType === 'image') { FileIconComponent = InsertDriveFileIcon; iconColor = '#3B82F6'; }
    else if (fileType === 'video') { FileIconComponent = InsertDriveFileIcon; iconColor = '#8B5CF6'; }
    else if (fileType === 'docx')  { FileIconComponent = DescriptionIcon; iconColor = '#2563EB'; }

    return (
      <Box
        key={index}
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1.5,
          borderRadius: '10px',
          border: '1px solid #F1F5F9',
          backgroundColor: '#fff',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: '#E2E8F0',
            backgroundColor: '#F8FAFC',
            transform: 'translateX(4px)',
          },
        }}
      >
        <Box
          sx={{
            width: 40, height: 40, borderRadius: '8px',
            backgroundColor: `${iconColor}10`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mr: 2, overflow: 'hidden', flexShrink: 0,
          }}
        >
          {fileType === 'image' ? (
            <img
              src={file.location}
              alt={fileName}
              style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
              onClick={() => handleImageClick(file.location)}
            />
          ) : (
            <FileIconComponent sx={{ color: iconColor, fontSize: '22px' }} />
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: '13.5px', fontWeight: 600, color: '#1E293B',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {fileName}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: '#64748B', fontWeight: 500 }}>
            {fileType.toUpperCase()} File
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.5} flexShrink={0}>
          <Tooltip title="Download">
            <IconButton
              size="small"
              onClick={() => handleDownloadFile(file)}
              sx={{ color: '#64748B', '&:hover': { color: '#0F172A', backgroundColor: '#F1F5F9' } }}
            >
              <FileDownloadIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(file)}
              sx={{ color: '#94A3B8', '&:hover': { color: '#EF4444', backgroundColor: '#FEF2F2' } }}
            >
              <DeleteOutlineIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    );
  };

  return (
    <>
      <Box sx={{
        border: '1px solid #E2E8F0', borderRadius: '12px',
        overflow: 'hidden', backgroundColor: '#FFFFFF',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        display: 'flex', flexDirection: 'column',
        maxHeight: 420,
      }}>
        {/* Header */}
        <Box sx={{
          px: 2, py: 1.5,
          borderBottom: '1px solid #E2E8F0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          backgroundColor: '#F8FAFC', flexShrink: 0,
        }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{
              width: 32, height: 32, borderRadius: '8px',
              background: '#CCFBF1', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
              <AttachFileIcon sx={{ color: '#0D9488', fontSize: '18px' }} />
            </Box>
            <Typography sx={{ fontSize: '15px', color: '#1E293B', fontWeight: 800, letterSpacing: '0.01em' }}>
              Attachments ({files?.length || 0})
            </Typography>
          </Stack>

          <Button
            startIcon={loading ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : <AddIcon />}
            variant="contained"
            disableElevation
            size="small"
            onClick={handleAddIconClick}
            disabled={loading}
            sx={{
              textTransform: 'none',
              backgroundColor: loading ? '#64748B' : '#0D9488',
              color: '#fff',
              borderRadius: '8px',
              px: 2,
              fontWeight: 600,
              '&:hover': { backgroundColor: loading ? '#64748B' : '#0F766E' },
              '&.Mui-disabled': { backgroundColor: '#64748B', color: '#fff' },
              transition: 'background-color 0.2s',
            }}
          >
            {loading ? 'Uploading…' : 'Add File'}
          </Button>

          <input
            type="file"
            hidden
            ref={fileInputRef}
            onChange={(e) => {
              const selectedFile = e.target.files[0];
              if (selectedFile) onAddFile(taskId, selectedFile);
              e.target.value = '';
            }}
          />
        </Box>

        {/* Scrollable file list */}
        <Box sx={{ overflowY: 'auto', flexGrow: 1, p: 2 }}>
          <Stack spacing={1}>
            {files?.length > 0
              ? files.map((file, index) => renderFileItem(file, index))
              : !loading && (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography sx={{ color: '#94A3B8', fontSize: '14px', fontWeight: 500 }}>
                      No attachments found
                    </Typography>
                  </Box>
                )
            }

            {/* Uploading row — appears at the bottom while upload is in progress */}
            {loading && (
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  p: 1.5,
                  borderRadius: '10px',
                  border: '1.5px dashed #CBD5E1',
                  background: '#F8FAFC',
                }}
              >
                <Box
                  sx={{
                    width: 40, height: 40, borderRadius: '8px',
                    bgcolor: '#F1F5F9',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <CircularProgress size={18} thickness={5} sx={{ color: '#0D9488' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                    Uploading file…
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: '#94A3B8' }}>
                    Please wait
                  </Typography>
                </Box>
              </Box>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Delete confirmation dialog */}
      <Dialog open={!!fileToDelete} onClose={handleCancelDelete} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '15px', fontWeight: 700, pb: 1 }}>
          Delete Attachment
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px', color: '#475569' }}>
            Are you sure you want to delete{' '}
            <Box component="span" sx={{ fontWeight: 700, color: '#0F172A' }}>
              {fileToDelete?.fileName || 'this file'}
            </Box>
            ? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            size="small"
            sx={{ textTransform: 'none', borderColor: '#E2E8F0', color: '#475569', borderRadius: '8px' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            size="small"
            sx={{ textTransform: 'none', backgroundColor: '#EF4444', borderRadius: '8px', '&:hover': { backgroundColor: '#DC2626' } }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image preview dialog */}
      <Dialog open={openImageDialog} onClose={() => setOpenImageDialog(false)} maxWidth="md">
        <img src={selectedImage} alt="Selected" style={{ width: '100%' }} />
      </Dialog>
    </>
  );
};

export default Attachments;
